import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

export type DemoRole = "kid" | "tween" | "teen" | "adult" | "parent" | "coach" | "school" | "agent" | "manager" | "vendor" | "casting_professional" | "casting_associate" | "producer" | "studio" | "hmu_artist" | "crew_member" | "writer" | "capture_creator";

interface DemoContextType {
  isDemoMode: boolean;
  demoRole: DemoRole | null;
  demoName: string;
  isWriteBlocked: boolean;
  isAIBlocked: boolean;
  enterDemoMode: (role: DemoRole, name?: string) => void;
  exitDemoMode: () => void;
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

const demoNames: Record<DemoRole, string> = {
  kid: "Alex",
  tween: "Sam",
  teen: "Jordan",
  adult: "Taylor",
  parent: "Chris",
  coach: "Coach Morgan",
  school: "Academy Arts",
  agent: "Agent Riley",
  manager: "Manager Taylor",
  vendor: "Vendor Val",
  casting_professional: "Director Casey",
  casting_associate: "Associate Jamie",
  producer: "Producer Morgan",
  studio: "Studio Exec Dana",
  hmu_artist: "Stylist Reese",
  crew_member: "Crew Alex",
  writer: "Writer Sage",
  capture_creator: "Creator Avery",
};

export const DemoProvider = ({ children }: { children: ReactNode }) => {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoRole, setDemoRole] = useState<DemoRole | null>(null);
  const [demoName, setDemoName] = useState("");

  // In demo mode, all writes and AI calls are blocked
  const isWriteBlocked = isDemoMode;
  const isAIBlocked = isDemoMode;

  const enterDemoMode = useCallback((role: DemoRole, name?: string) => {
    setIsDemoMode(true);
    setDemoRole(role);
    setDemoName(name || demoNames[role]);
  }, []);

  const exitDemoMode = useCallback(() => {
    setIsDemoMode(false);
    setDemoRole(null);
    setDemoName("");
  }, []);

  // Clear demo state on page unload/reload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isDemoMode) {
        exitDemoMode();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDemoMode, exitDemoMode]);

  return (
    <DemoContext.Provider
      value={{
        isDemoMode,
        demoRole,
        demoName,
        isWriteBlocked,
        isAIBlocked,
        enterDemoMode,
        exitDemoMode,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
};

/** Returns demo mode state and controls; blocks writes and AI when active. */
export const useDemo = () => {
  const context = useContext(DemoContext);
  if (context === undefined) {
    throw new Error("useDemo must be used within a DemoProvider");
  }
  return context;
};
